import { useMutation } from '@apollo/client'
import { useNavigate } from '@faceup/router'
import { Button, Popconfirm, Space } from '@faceup/ui-base'
import { Institution } from '@faceup/utils'
import { useState } from 'react'
import { graphql } from '../__generated__'

const mutations = {
  ApproveSchoolRegistration: graphql(`
    mutation ApproveSchoolRegistrationMutation(
      $input: ApproveSchoolRegistrationInput!
    ) {
      approveSchoolRegistration(input: $input) {
        company {
          id
          registered
          isVerified

          billing {
            id
            plan
          }
        }
      }
    }
  `),
  RejectSchoolRegistration: graphql(`
    mutation RejectSchoolRegistrationMutation(
      $input: RejectSchoolRegistrationInput!
    ) {
      rejectSchoolRegistration(input: $input) {
        company {
          id
          billing {
            id
            plan
          }

          isVerified

          members(page: 0, rowsPerPage: 1000) {
            items {
              id
              name
              email
              phone
            }
          }
        }
      }
    }
  `),
}

type Props = {
  institutionId: string | null
  type: Institution | null
}

const RegistrationButtons = ({ institutionId, type }: Props) => {
  const [loadingButton, setLoadingButton] = useState<'verify' | 'reject' | null>()
  const navigate = useNavigate()

  const [rejectSchool, { loading: loadingReject }] = useMutation(
    mutations.RejectSchoolRegistration,
    {
      onError: () => {
        setLoadingButton(null)
      },
      onCompleted: () =>
        navigate(routes => (type === Institution.Company ? routes.companies() : routes.schools())),
    }
  )

  const [approveSchool, { loading: loadingVerify }] = useMutation(
    mutations.ApproveSchoolRegistration,
    {
      onError: () => {
        setLoadingButton(null)
      },
      onCompleted: () => setLoadingButton(null),
    }
  )

  if (!institutionId) {
    return null
  }

  return (
    <Space>
      <Popconfirm
        title='Rejecting the registration will send emails to institution'
        onConfirm={() => {
          setLoadingButton('reject')
          rejectSchool({
            variables: {
              input: {
                organizationalUnitId: institutionId,
              },
            },
          })
        }}
      >
        <Button
          disabled={loadingReject && loadingButton === 'verify'}
          loading={loadingButton === 'reject'}
          ghost
          type='primary'
        >
          Reject registration
        </Button>
      </Popconfirm>

      <Popconfirm
        title='Approving the registration will send emails to institution'
        onConfirm={() => {
          setLoadingButton('verify')
          approveSchool({
            variables: {
              input: {
                companyId: institutionId,
              },
            },
          })
        }}
      >
        <Button
          type='primary'
          disabled={loadingVerify && loadingButton === 'reject'}
          loading={loadingButton === 'verify'}
        >
          Verify registration
        </Button>
      </Popconfirm>
    </Space>
  )
}

export default RegistrationButtons
